import { defineComponent, ref, computed } from 'vue-demi';
import { openBlock, createElementBlock, normalizeClass, createCommentVNode, createElementVNode, toDisplayString, renderSlot } from 'vue';

var EmptyStateSize;
(function (EmptyStateSize) {
  EmptyStateSize["L"] = "l";
  EmptyStateSize["M"] = "m";
})(EmptyStateSize || (EmptyStateSize = {}));

const EmptyStateRoot = [
// Layout
'grow', 'flex', 'flex-col', 'justify-center', 'items-center',
// Box
'w-full',
// Spacing
'gap-sm'];
const EmptyStateImage = [
// Spacing
'mb-sm'];
const EmptyStateImageSizes = {
  [EmptyStateSize.L]: [
  // Box
  'h-3xl'],
  [EmptyStateSize.M]: [
  // Box
  'h-2xl']
};
const EmptyStateHeading = [
// Theme
'theme-foreground-default', 'dark:theme-foreground-default-dark'];
const EmptyStateHeadingSizes = {
  [EmptyStateSize.L]: [
  // Typography
  'typography-headline-2'],
  [EmptyStateSize.M]: [
  // Typography
  'typography-headline-4']
};
const EmptyStateText = ['text-center',
// Theme
'text-outline', 'dark:text-outline-dark'];
const EmptyStateContent = EmptyStateText;
const EmptyStateTextSizes = {
  [EmptyStateSize.L]: [
  // Typography
  'typography-body-2-regular'],
  [EmptyStateSize.M]: [
  // Typography
  'typography-caption-regular']
};
const EmptyStateContentSizes = EmptyStateTextSizes;

/**
 * The component to display when there is no content on a page,
 * no records found or selected, or while the data is being loaded.
 * Provides a hint to the user on what to do.
 * Consists of text message, optional icon and optional loader.
 */
var script = defineComponent({
  name: 'OrEmptyState',
  props: {
    /**
     * Size of the component, affects font and icon size.
     * Use Large if the component takes all screen space,
     * and Medium if it is a part of the complex layout.
     */
    size: {
      type: String,
      default: EmptyStateSize.M
    },
    /**
     * The heading of the empty state block.
     */
    heading: {
      type: String,
      default: ''
    },
    /**
     * Additional text such as a hint for a user with proposed next steps.
     */
    text: {
      type: String,
      default: ''
    },
    /**
     * Path to the image to display above the heading.
     * Image height is fixed depending on `size` prop, width is undefined to preserve aspect ratio.
     */
    imageSrc: {
      type: String,
      default: undefined
    }
  },
  setup(props) {
    const root = ref();
    const rootClasses = computed(() => ['or-empty-state-v3', ...EmptyStateRoot]);
    const imageClasses = computed(() => ['or-empty-state_image', ...EmptyStateImage, ...EmptyStateImageSizes[props.size]]);
    const headingClasses = computed(() => ['or-empty-state_heading', ...EmptyStateHeading, ...EmptyStateHeadingSizes[props.size]]);
    const textClasses = computed(() => ['or-empty-state_text', ...EmptyStateText, ...EmptyStateTextSizes[props.size]]);
    const slotClasses = computed(() => ['or-empty-state_content', ...EmptyStateContent, ...EmptyStateContentSizes[props.size]]);
    return {
      root,
      rootClasses,
      imageClasses,
      headingClasses,
      textClasses,
      slotClasses
    };
  }
});

const _hoisted_1 = ["src"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    ref: "root",
    class: normalizeClass(_ctx.rootClasses)
  }, [_ctx.imageSrc ? (openBlock(), createElementBlock("img", {
    key: 0,
    src: _ctx.imageSrc,
    class: normalizeClass(_ctx.imageClasses),
    alt: ""
  }, null, 10 /* CLASS, PROPS */, _hoisted_1)) : createCommentVNode("v-if", true), createElementVNode("div", {
    class: normalizeClass(_ctx.headingClasses)
  }, toDisplayString(_ctx.heading), 3 /* TEXT, CLASS */), _ctx.$slots.default ? (openBlock(), createElementBlock("div", {
    key: 1,
    class: normalizeClass(_ctx.slotClasses)
  }, [createCommentVNode(" @slot Additional content can be inserted via default slot. The slot uses the same font as a `text` prop. "), renderSlot(_ctx.$slots, "default")], 2 /* CLASS */)) : createCommentVNode("v-if", true), createElementVNode("div", {
    class: normalizeClass(_ctx.textClasses)
  }, toDisplayString(_ctx.text), 3 /* TEXT, CLASS */)], 2 /* CLASS */);
}

script.render = render;
script.__file = "src/components/or-empty-state-v3/OrEmptyState.vue";

export { EmptyStateSize as E, script as s };
